import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiSend,
  FiMapPin,
  FiPhone,
  FiCheck,
  FiAlertCircle,
  FiMail
} from 'react-icons/fi';
import { saveEmailToGoogleSheets, validateEmail } from '../utils/googleSheets';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // idle, loading, success, error
  const [statusMessage, setStatusMessage] = useState('');
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email || !validateEmail(email)) {
      setSubscriptionStatus('error');
      setStatusMessage('Please enter a valid email address');
      setTimeout(() => setSubscriptionStatus('idle'), 3000);
      return;
    }

    setSubscriptionStatus('loading');
    setStatusMessage('Subscribing...');

    try {
      const success = await saveEmailToGoogleSheets(email);
      
      if (success) {
        setSubscriptionStatus('success');
        setStatusMessage('Successfully subscribed!');
        setEmail('');
        setTimeout(() => setSubscriptionStatus('idle'), 4000);
      } else {
        setSubscriptionStatus('error');
        setStatusMessage('Subscription failed. Please try again.');
        setTimeout(() => setSubscriptionStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscriptionStatus('error');
      setStatusMessage('An error occurred. Please try again.');
      setTimeout(() => setSubscriptionStatus('idle'), 3000);
    }
  };

  const footerLinks = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing', hidden: true },
      { name: 'Releases', href: '/releases', isRoute: true },
    ],
    company: [
      { name: 'About Us', href: '#about', isAnchor: true },
      { name: 'Blog', href: '/blog', isRoute: true },
      { name: 'Contact', href: '/contact', isRoute: true },
    ],
    support: [
      { name: 'Help Center', href: '/guide', isRoute: true },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/terms', isRoute: true },
      { name: 'Terms of Service', href: '/terms', isRoute: true },
      { name: 'Cookie Policy', href: '#' },
    ],
  };

  return (
    <footer className="relative py-20 px-6 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-800 to-transparent"></div>
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 360, 0]
          }}
          transition={{ 
            duration: 40, 
            repeat: Infinity, 
            ease: "linear" 
          }}
          className="absolute top-1/4 left-1/6 w-64 h-64 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [360, 0, 360]
          }}
          transition={{ 
            duration: 45, 
            repeat: Infinity, 
            ease: "linear" 
          }}
          className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-gradient-to-r from-accent-500/10 to-primary-500/10 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto relative z-10">
        {/* Newsletter Section - Only show on home page */}
        {isHomePage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 p-12 glass border border-white/10 rounded-3xl"
          >
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl md:text-4xl font-display font-bold mb-4"
          >
            <span className="gradient-text">Stay Updated</span>
          </motion.h3>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-gray-300 mb-8 max-w-2xl mx-auto"
          >
            Get the latest updates on new AI models, features, and productivity tips delivered to your inbox.
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 max-w-md mx-auto"
          >
            <div className="relative flex-1 w-full">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-secondary-500 transition-colors duration-300"
                required
              />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-secondary-500/20 to-accent-500/20 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
            
            <motion.button
              whileHover={{ scale: subscriptionStatus === 'loading' ? 1 : 1.05 }}
              whileTap={{ scale: subscriptionStatus === 'loading' ? 1 : 0.95 }}
              type="submit"
              disabled={subscriptionStatus === 'loading' || subscriptionStatus === 'success'}
              className={`px-6 py-4 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 min-w-[140px] justify-center ${
                subscriptionStatus === 'success' 
                  ? 'bg-gradient-to-r from-green-500 to-green-600' 
                  : subscriptionStatus === 'error'
                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                  : 'bg-gradient-to-r from-secondary-500 to-accent-500'
              } ${
                subscriptionStatus === 'loading' ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {subscriptionStatus === 'loading' ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Subscribing...</span>
                </>
              ) : subscriptionStatus === 'success' ? (
                <>
                  <FiCheck className="w-5 h-5" />
                  <span>Subscribed!</span>
                </>
              ) : subscriptionStatus === 'error' ? (
                <>
                  <FiAlertCircle className="w-5 h-5" />
                  <span>Try Again</span>
                </>
              ) : (
                <>
                  <FiSend className="w-5 h-5" />
                  <span>Subscribe</span>
                </>
              )}
            </motion.button>
          </motion.form>

          {/* Status Message */}
          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mt-4 text-sm font-medium ${
                subscriptionStatus === 'success' 
                  ? 'text-green-400' 
                  : subscriptionStatus === 'error'
                  ? 'text-red-400'
                  : 'text-gray-300'
              }`}
            >
              {statusMessage}
            </motion.div>
          )}
        </motion.div>
        )}

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg bg-gradient-to-r from-secondary-500 to-accent-500">
                <img 
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="Bervin AI" 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = '<span class="text-white font-bold text-xl flex items-center justify-center w-full h-full">B</span>';
                  }}
                />
              </div>
              <span className="text-3xl font-display font-bold gradient-text">
                Bervin
              </span>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              The ultimate AI conversation platform. Talk to multiple AIs in one place 
              and unlock unprecedented productivity and creativity.
            </p>

            {/* Contact Info */}
            <div className="space-y-3 text-gray-400">
              <div className="flex items-center space-x-3">
                <FiMapPin className="w-4 h-4 text-secondary-400" />
                <span className="text-sm">Bangalore, India</span>
              </div>
              <div className="flex items-center space-x-3">
                <FiPhone className="w-4 h-4 text-accent-400" />
                <div className="flex flex-col text-sm">
                  <span>+91 7448378077</span>
                  <span>+91 7406131262</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <FiMail className="w-4 h-4 text-primary-400" />
                <span className="text-sm">bervin.helpdesk@gmail.com</span>
              </div>
            </div>
          </motion.div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 + categoryIndex * 0.1 }}
            >
              <h4 className="text-white font-semibold text-lg mb-4 capitalize">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.filter((link) => !link.hidden).map((link, linkIndex) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.3 + categoryIndex * 0.1 + linkIndex * 0.05 }}
                  >
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        onClick={() => {
                          // Force immediate scroll to top with multiple fallbacks
                          setTimeout(() => {
                            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                            document.documentElement.scrollTop = 0;
                            document.body.scrollTop = 0;
                            if (window.pageYOffset !== 0) {
                              window.scroll(0, 0);
                            }
                          }, 0);
                        }}
                        className="text-gray-400 hover:text-white transition-colors duration-300 text-sm relative group inline-block"
                      >
                        <motion.span whileHover={{ x: 5 }} className="block">
                          {link.name}
                        </motion.span>
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary-500 to-accent-500 group-hover:w-full transition-all duration-300"></span>
                      </Link>
                    ) : (
                      <motion.a
                        href={link.href}
                        whileHover={{ x: 5 }}
                        className="text-gray-400 hover:text-white transition-colors duration-300 text-sm relative group"
                      >
                        {link.name}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary-500 to-accent-500 group-hover:w-full transition-all duration-300"></span>
                      </motion.a>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"
        ></motion.div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0"
        >
          {/* Copyright */}
          <div className="text-gray-400 text-sm">
            © 2024 Bervin AI. All rights reserved. Made with ❤️ for the AI community.
          </div>
        </motion.div>

        {/* Floating Elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 6, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-20 right-20 w-16 h-16 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-xl blur-lg"
        />
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-20 left-20 w-20 h-20 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-lg"
        />
      </div>
    </footer>
  );
};

export default Footer;
